import { printFlagsList } from '@/layout/pages/EnergyXplorer/printFlags';
import ChildAnalysisTab from '@/layout/pages/EnergyXplorer/Tabs/ChildAnalysisTab';
import ComparisonAnalysisTab from '@/layout/pages/EnergyXplorer/Tabs/ComparisonAnalysisTab';
import DurationAnalysisTab from '@/layout/pages/EnergyXplorer/Tabs/DurationAnalysisTab';
import EnergyAnalysisTab from '@/layout/pages/EnergyXplorer/Tabs/EnergyAnalysisTab';
import EnergyOverviewTab from '@/layout/pages/EnergyXplorer/Tabs/EnergyOverviewTab';
import EnergyTreeGraphTab from '@/layout/pages/EnergyXplorer/Tabs/EnergyTreeGraphTab';
import EsgOverviewTab from '@/layout/pages/EnergyXplorer/Tabs/EsgOverviewTab';
import HourAnalysisTab from '@/layout/pages/EnergyXplorer/Tabs/HourAnalysisTab';
import InitiativesTab from '@/layout/pages/EnergyXplorer/Tabs/InitiativesTab';
import PowerLoadProfilesAnalysisTab from '@/layout/pages/EnergyXplorer/Tabs/PowerLoadProfilesAnalysisTab';
import StandardAnalysisTab from '@/layout/pages/EnergyXplorer/Tabs/StandardAnalysisTab';
import CloseIcon from '@mui/icons-material/Close';
import PrintRoundedIcon from '@mui/icons-material/PrintRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';

import {
  AppBar,
  Box,
  Button,
  Dialog,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';
import React, { Suspense, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useReactToPrint } from 'react-to-print';

import DateRangeSelector from '@/components/time-range-selector/DateRangeSelector';
import PrintModule from '@/features/energyxplorer/components/print/PrintModule';
import PrintSettings from '@/features/energyxplorer/components/print/PrintSettings';
import PrintWrapper from '@/features/energyxplorer/components/print/PrintWrapper';

const PrintDialog = ({
  open,
  onClose,
  selectedAccount,
  setTimestampSummary,
  setTimestampSummarySetBy,
  selectedEnergyGroup,
  timeRange,
  setTimeRange,
  setDuration,
  timestampSummary,
  timestampSummarySetBy,
  units,
  // EnergyAnalysisTab
  nodeToAnalyse,
  seuToAnalyze,
  analysisMode,
  duration,
  pageToPrint = 'all',

  //
}) => {
  const [checkedStates, setCheckedStates] = useState([true]);
  const contentRef = useRef(null);
  const reactToPrintFn = useReactToPrint({ contentRef });
  const handleCheckedChange = (idx, checked) => {
    setCheckedStates((prev) => {
      const next = [...prev];
      next[idx] = checked;
      return next;
    });
  };
  const keys = [
    'consumption_overview',
    'esg',
    'energy_breakdown',
    'energy_tree',
    'energy_tree_overview',
    'energy_analysis',
    'energy_analysis_breakdown',
    'energy_analysis_hour',
    'energy_analysis_duration',
    'energy_analysis_node',
    'energy_analysis_compare',
    'energy_analysis_load_profile',
    'initiatives',
  ];

  const [settingsList, setSettingsList] = useState(
    keys.map((key) => [
      {
        key,
        textfield: '',
        printFlags: printFlagsList[key], // assumes printFlagsList[key] exists
      },
    ]),
  );
  const handleSettingsChange = (id, index, change) => {
    setSettingsList((prev) => {
      const result = [...prev];
      result[id] = change;
      return result;
    });
  };

  const handleCopy = (id) => {
    setSettingsList((prev) => {
      const next = [...prev];
      next[id] = [...next[id], next[id][0]];
      return next;
    });
  };
  const handleRemove = (id, index) => {
    setSettingsList((prev) => {
      const next = [...prev];
      next[id] = next[id].filter((_, idx) => index !== idx);
      return next;
    });
  };

  const { t } = useTranslation('print');

  const modules = useMemo(() => {
    return [
      {
        key: 'consumption_overview',
        Component: EnergyOverviewTab,
        props: {
          selectedAccount,
          selectedEnergyGroup,
          timeRange,
          handleSEURowClick: () => {},
          setTimestampSummary,
          setTimestampSummarySetBy,
        },
      },
      {
        key: 'esg',
        Component: EsgOverviewTab,
        props: {
          selectedAccount,
          selectedEnergyGroup,
          timeRange,
          setTimeRange,
        },
      },
      {
        key: 'energy_tree',
        Component: (props) => (
          <Box maxHeight="600px">
            <EnergyTreeGraphTab {...props} />
          </Box>
        ),
        props: {
          selectedEnergyGroup,
          units,
          timeRange,
          setTimestampSummary,
          setTimestampSummarySetBy,
          setNodeToAnalyse: () => {},
          setValue: () => {},
        },
      },
      {
        key: 'energy_tree_overview',
        Component: StandardAnalysisTab,
        props: {
          selectedAccount,
          selectedEnergyGroup,
          timeRange,
          setTimestampSummary,
          setTimestampSummarySetBy,
        },
      },
      {
        key: 'energy_analysis_node',
        Component: EnergyAnalysisTab,
        props: {
          selectedAccount,
          focusedNode: nodeToAnalyse,
          focusedSeu: seuToAnalyze,
          initialMode: analysisMode,
          selectedEnergyGroup,
          setTimestampSummary,
          setTimestampSummarySetBy,
          duration,
          timeRange,
        },
      },
      {
        key: 'energy_analysis_compare',
        Component: ComparisonAnalysisTab,
        props: {
          selectedAccount,
          selectedEnergyGroup,
          setTimestampSummary,
          setTimestampSummarySetBy,
          timeRange,
          duration,
        },
      },
      {
        key: 'energy_analysis_duration',
        Component: DurationAnalysisTab,
        props: {
          selectedAccount,
          selectedEnergyGroup,
          setTimestampSummary,
          setTimestampSummarySetBy,
          timeRange,
        },
      },
      {
        key: 'energy_analysis_breakdown',
        Component: ChildAnalysisTab,
        props: {
          selectedAccount,
          selectedEnergyGroup,
          setTimestampSummary,
          setTimestampSummarySetBy,
          timeRange,
        },
      },
      {
        key: 'energy_analysis_load_profile',
        Component: PowerLoadProfilesAnalysisTab,
        props: {
          selectedAccount,
          selectedEnergyGroup,
          timeRange,
          setTimestampSummary,
          setTimestampSummarySetBy,
        },
      },
      {
        key: 'energy_analysis_hour',
        Component: HourAnalysisTab,
        props: {
          selectedAccount,
          selectedEnergyGroup,
          setTimestampSummary,
          setTimestampSummarySetBy,
          timeRange,
        },
      },
      {
        key: 'initiatives',
        Component: InitiativesTab,
        props: {
          selectedEnergyGroup,
        },
      },
    ]
      .map((module, index) => {
        return {
          ...module,
          id: index,
          title: t(`print_modules.${module.key}.title`),
          description: t(`print_modules.${module.key}.description`),
          settingsSlot: (
            <Suspense fallback={<Skeleton />}>
              <PrintSettings
                settings={settingsList[index] ?? []}
                onSettingsChange={handleSettingsChange}
                onCopy={handleCopy}
                id={index}
                onRemove={handleRemove}
              />
            </Suspense>
          ),
        };
      })
      .filter((module) => {
        if (pageToPrint === 'all') return true;
        return module.key === pageToPrint;
      });
  }, [
    analysisMode,
    duration,
    nodeToAnalyse,
    pageToPrint,
    selectedAccount,
    selectedEnergyGroup,
    setTimeRange,
    setTimestampSummary,
    setTimestampSummarySetBy,
    settingsList,
    seuToAnalyze,
    t,
    timeRange,
    units,
  ]);
  const [isPreview, setIsPreview] = useState(false);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{
        sx: { p: 0, m: 0, borderRadius: 0 },
      }}
    >
      <AppBar elevation={0} sx={{ position: 'relative', height: '5vh' }}>
        <Toolbar variant="dense" sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="h7" component="div">
              {t('header')}
            </Typography>
          </Box>

          <IconButton
            color="inherit"
            onClick={onClose}
            aria-label="close"
            size="small"
            sx={{
              mr: -2,
            }}
          >
            <CloseIcon color="inherit" />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Box sx={{ display: 'flex', height: '95vh' }}>
        <Box
          display={'flex'}
          flex={1.5}
          justifyContent={'space-around'}
          sx={{ bgcolor: 'grey.100' }}
        >
          {/* Left column (empty for now) */}
          <Box
            ref={contentRef}
            width={'1050px'}
            my={2}
            sx={{ bgcolor: 'background.paper', overflow: 'scroll' }}
          >
            {checkedStates.every((b) => b === false) ? (
              <Typography
                variant="h4"
                align="center"
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                {t('choose')}
              </Typography>
            ) : (
              modules.map((module, idx) => {
                if (checkedStates[idx]) {
                  return (
                    <Suspense key={idx} fallback={<Skeleton />}>
                      {settingsList[idx]
                        ? settingsList[idx].map((s, index) => {
                            return (
                              <PrintWrapper
                                key={`${idx}_${index}`}
                                title={module.title}
                                description={s?.textfield ?? ''}
                                isPreview={isPreview}
                              >
                                <module.Component
                                  {...module.props}
                                  printFlags={s?.printFlags}
                                />
                              </PrintWrapper>
                            );
                          })
                        : null}
                    </Suspense>
                  );
                } else {
                  return <Box key={idx} />;
                }
              })
            )}
          </Box>
        </Box>
        {/* Divider */}
        <Divider orientation="vertical" flexItem />
        {/* Right column with PrintModules */}
        <Box
          sx={{
            flex: 0.7,
            p: 4,
            overflowY: 'auto',
            bgcolor: 'grey.50',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          <Box display={'flex'} justifyContent={'flex-end'} mb={2}>
            <Suspense fallback={<Skeleton />}>
              <DateRangeSelector
                setTimeRange={setTimeRange}
                timeRange={timeRange}
                setDuration={setDuration}
                timestampSummary={timestampSummary}
                timestampSummarySetBy={timestampSummarySetBy}
              />
            </Suspense>
          </Box>

          <Stack flex={1} overflow={'auto'}>
            {modules.map((mod, idx) => (
              <Suspense key={idx} fallback={<Skeleton />}>
                <PrintModule
                  key={mod.title}
                  title={mod.title}
                  description={mod.description}
                  settingsSlot={mod.settingsSlot}
                  checked={checkedStates[idx]}
                  onCheckedChange={(checked) =>
                    handleCheckedChange(idx, checked)
                  }
                />
              </Suspense>
            ))}
          </Stack>

          <Box
            height={'30px'}
            mt={2}
            justifyContent={'flex-end'}
            display={'flex'}
          >
            <Button
              sx={{ mr: 2 }}
              variant={'contained'}
              onClick={() => setIsPreview((prev) => !prev)}
              endIcon={
                isPreview ? <EditRoundedIcon /> : <VisibilityRoundedIcon />
              }
            >
              {isPreview ? t('buttons.editview') : t('buttons.preview')}
            </Button>
            <Button
              variant="contained"
              onClick={reactToPrintFn}
              endIcon={<PrintRoundedIcon />}
            >
              {t('buttons.print')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default PrintDialog;
